import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { mixpanel } from '../lib/mixpanel';

export interface User {
  id: string;
  email: string;
  businessName: string;
  businessCategory: string;
  mobile?: string;
  createdAt: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  // ── Email + password ─────────────────────────────────────────────────────
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, businessName: string, businessCategory: string, verificationToken: string, mobile?: string) => Promise<User>;
  // ── Email verification ────────────────────────────────────────────────────
  sendEmailVerification: (email: string) => Promise<void>;
  verifyEmailOTP: (email: string, otp: string) => Promise<string>; // returns verificationToken
  // ── Password reset ────────────────────────────────────────────────────────
  sendPasswordResetOTP: (email: string) => Promise<void>;
  resetPassword: (email: string, otp: string, newPassword: string) => Promise<void>;
  // ── OAuth callback ────────────────────────────────────────────────────────
  storeTokens: (accessToken: string, refreshToken: string) => Promise<User | null>;
  // ── Dev bypass ────────────────────────────────────────────────────────────
  bypassLogin: () => void;
  // ── Common ────────────────────────────────────────────────────────────────
  logout: () => void;
}

// API base URL injected at build time via REACT_APP_API_URL.
// Docker (nginx proxy): /api  — relative, nginx forwards to backend.
// Local dev without Docker: http://localhost:3001/api
export const API = process.env.REACT_APP_API_URL ?? 'http://localhost:3001/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Guest-claim return routing
// ---------------------------------------------------------------------------
// ProjectDetail's out-of-credit modal stores both the return path and the
// guest user id it belongs to before sending the visitor to sign up. Only
// honor the return path if the session that just finished authenticating is
// that same guest — otherwise (existing Google account, 409 on register,
// etc.) the new session doesn't own that project and would 404 on it.
export function consumeGuestReturnTo(newUserId?: string | null): string {
  const returnTo = sessionStorage.getItem('guestReturnTo');
  const claimUserId = sessionStorage.getItem('guestClaimUserId');
  sessionStorage.removeItem('guestReturnTo');
  sessionStorage.removeItem('guestClaimUserId');
  return returnTo && claimUserId && claimUserId === newUserId ? returnTo : '/home';
}

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

function applyTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
}

function clearTokens() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  delete axios.defaults.headers.common['Authorization'];
}

// ---------------------------------------------------------------------------
// Axios interceptor — silent token refresh on 401
// ---------------------------------------------------------------------------

let isRefreshing = false;
let refreshQueue: Array<{ resolve: (t: string) => void; reject: (e: unknown) => void }> = [];

function processQueue(err: unknown, token: string | null) {
  refreshQueue.forEach(({ resolve, reject }) => (err ? reject(err) : resolve(token!)));
  refreshQueue = [];
}

function createResponseInterceptor(onBlocked?: () => void) {
  return axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      const original = error.config;
      // Handle account blocked — do not attempt refresh, clear session
      const errBody = error.response?.data;
      if (
        errBody?.error === 'ACCOUNT_BLOCKED' ||
        (typeof errBody?.message === 'string' && errBody.message.includes('ACCOUNT_BLOCKED'))
      ) {
        clearTokens();
        localStorage.setItem('blocked', 'true');
        onBlocked?.();
        return Promise.reject(error);
      }
      if (
        error.response?.status === 401 &&
        !original._retry &&
        !original.url?.endsWith('/auth/refresh')
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({
              resolve: (token) => {
                original.headers['Authorization'] = `Bearer ${token}`;
                resolve(axios(original));
              },
              reject,
            });
          });
        }
        original._retry  = true;
        isRefreshing     = true;
        const stored     = localStorage.getItem('refreshToken');
        if (!stored) {
          clearTokens();
          isRefreshing = false;
          return Promise.reject(error);
        }
        try {
          const res = await axios.post(`${API}/auth/refresh`, { refreshToken: stored });
          const { accessToken, refreshToken } = res.data;
          applyTokens(accessToken, refreshToken);
          processQueue(null, accessToken);
          original.headers['Authorization'] = `Bearer ${accessToken}`;
          return axios(original);
        } catch (err) {
          processQueue(err, null);
          clearTokens();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    },
  );
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser]       = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const interceptorId = createResponseInterceptor(() => setUser(null));
    return () => { axios.interceptors.response.eject(interceptorId); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      applyTokens(token, localStorage.getItem('refreshToken') ?? '');
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchProfile = async (): Promise<User | null> => {
    try {
      const res = await axios.get(`${API}/user/profile`);
      setUser(res.data);
      mixpanel.identify(res.data.id);
      return res.data as User;
    } catch (err: any) {
      // Only clear tokens on genuine auth failures (401/403 that survived the refresh attempt).
      // Network errors or 5xx keep tokens intact so a page reload can recover without logging out.
      const status = err?.response?.status;
      if (!status || status === 401 || status === 403) {
        clearTokens();
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ── Email / password ───────────────────────────────────────────────────

  const login = async (email: string, password: string): Promise<void> => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { user: u, tokens } = res.data;
    applyTokens(tokens.accessToken, tokens.refreshToken);
    setUser(u);
    mixpanel.identify(u.id);
  };

  const register = async (
    email: string,
    password: string,
    businessName: string,
    businessCategory: string,
    verificationToken: string,
    mobile?: string,
  ): Promise<User> => {
    const res = await axios.post(`${API}/auth/register`, {
      email, password, businessName, businessCategory, verificationToken,
      ...(mobile ? { mobile } : {}),
    });
    const { user: u, tokens } = res.data;
    applyTokens(tokens.accessToken, tokens.refreshToken);
    setUser(u);
    mixpanel.identify(u.id);
    return u;
  };

  // ── Email verification ─────────────────────────────────────────────────

  const sendEmailVerification = async (email: string): Promise<void> => {
    await axios.post(`${API}/auth/email/send-verification`, { email });
  };

  const verifyEmailOTP = async (email: string, otp: string): Promise<string> => {
    const res = await axios.post(`${API}/auth/email/verify`, { email, otp });
    return res.data.verificationToken as string;
  };

  // ── Password reset ─────────────────────────────────────────────────────

  const sendPasswordResetOTP = async (email: string): Promise<void> => {
    await axios.post(`${API}/auth/forgot-password`, { email });
  };

  const resetPassword = async (email: string, otp: string, newPassword: string): Promise<void> => {
    await axios.post(`${API}/auth/reset-password`, { email, otp, newPassword });
  };

  // ── OAuth callback ─────────────────────────────────────────────────────

  const storeTokens = async (accessToken: string, refreshToken: string): Promise<User | null> => {
    applyTokens(accessToken, refreshToken);
    return fetchProfile();
  };

  // ── Dev bypass ─────────────────────────────────────────────────────────

  const bypassLogin = () => {
    const mockAccess  = 'dev-bypass-token-' + Date.now();
    const mockRefresh = 'dev-bypass-refresh-' + Date.now();
    applyTokens(mockAccess, mockRefresh);
    // Actual profile is fetched by storeTokens → fetchProfile; this is just an optimistic placeholder
    fetchProfile();
  };

  const logout = () => {
    clearTokens();
    setUser(null);
    mixpanel.reset();
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, register,
      sendEmailVerification, verifyEmailOTP,
      sendPasswordResetOTP, resetPassword,
      storeTokens,
      bypassLogin,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
